import React, { useState, useEffect } from "react";
import { Switch } from "@headlessui/react";
import { PencilIcon } from "@heroicons/react/24/outline";
import '../../adminComponents/ReportTable.css'


const API_URL = process.env.REACT_APP_API_URL || "https://didrouteapi.com/api";

const AssignTargetPopup = ({ campaign, onClose, onUpdateTarget }) => {
  const [showForm, setShowForm] = useState(false);
  const [targets, setTargets] = useState([]);
  const [buyers, setBuyers] = useState([]);
  const [formData, setFormData] = useState({
    buyer_id: "",
    targetName: "",
    targetNumber: "",
    maxConcurrency: "",
    monthlyCap: "",
    dailyCap: "",
    weeklyCap: "",
    connectionTimeout: "60",
    priority: "",
  });

  const [showMonthlyCap, setShowMonthlyCap] = useState(false);
  const [showDailyCap, setShowDailyCap] = useState(false);
  const [showWeeklyCap, setShowWeeklyCap] = useState(false);
  const [showMaxConcurrency, setShowMaxConcurrency] = useState(false);
  const [editingPriority, setEditingPriority] = useState(null);
  const [newPriority, setNewPriority] = useState("");
  const [editingTarget, setEditingTarget] = useState(null);
  const [editingWeight, setEditingWeight] = useState(null);
  const [newWeight, setNewWeight] = useState("");

  useEffect(() => {
    const userId = localStorage.getItem("user_id");
    if (!userId) {
      console.warn("No user_id found in localStorage");
      // Optionally redirect to login or show error
    }
  }, []);

  useEffect(() => {
    fetchTargets();
    fetchBuyers();
  }, [campaign.id]);

  const fetchTargets = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${API_URL}/campaigns/${campaign.id}/targets`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const data = await response.json();
      if (data.success) {
        setTargets(data.data);
      }
    } catch (error) {
      console.error("Error fetching targets:", error);
    }
  };

  const fetchBuyers = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/buyers`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setBuyers(data.data);
      }
    } catch (error) {
      console.error("Error fetching buyers:", error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Add targetNumber to the numericFields array
    const numericFields = [
      "targetNumber",
      "maxConcurrency",
      "monthlyCap",
      "dailyCap",
      "weeklyCap",
      "connectionTimeout",
      "priority",
    ];

    if (numericFields.includes(name)) {
      // Remove non-numeric characters and trim whitespace
      const numericValue = value.replace(/[^\d]/g, '').trim();
      setFormData(prev => ({
        ...prev,
        [name]: numericValue
      }));
    } else {
      // For non-numeric fields, just trim whitespace
      setFormData(prev => ({
        ...prev,
        [name]: value.trim()
      }));
    }
  };

  const handleEdit = (target) => {
    setEditingTarget(target);
    setFormData({
      buyer_id: target.buyer_id.toString(),
      targetName: target.target_name,
      targetNumber: target.target_number,
      maxConcurrency: target.max_concurrency,
      monthlyCap: target.monthly_cap || "",
      dailyCap: target.daily_cap || "",
      weeklyCap: target.weekly_cap || "",
      connectionTimeout: target.connection_timeout || "60",
      priority: target.priority,
    });
    setShowMonthlyCap(target.monthly_cap_flag === "Yes");
    setShowDailyCap(target.daily_cap_flag === "Yes");
    setShowWeeklyCap(target.weekly_cap_flag === "Yes");
    setShowMaxConcurrency(target.cc_limit_flag === "Yes");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate all required fields
    if (
      !formData.buyer_id ||
      !formData.targetName ||
      !formData.targetNumber ||
      (showMaxConcurrency && !formData.maxConcurrency) ||
      !formData.connectionTimeout ||
      !formData.priority
    ) {
      alert("Please fill in all required fields");
      return;
    }

    const payload = {
      buyerId: parseInt(formData.buyer_id),
      targetName: formData.targetName,
      targetNumber: formData.targetNumber,
      maxConcurrency: parseInt(formData.maxConcurrency) || 0,
      connectionTimeout: parseInt(formData.connectionTimeout),
      priority: parseInt(formData.priority),
      monthlyCap: parseInt(formData.monthlyCap) || 0,
      dailyCap: parseInt(formData.dailyCap) || 0,
      weeklyCap: parseInt(formData.weeklyCap) || 0,
      cc_limit_flag: showMaxConcurrency ? "Yes" : "No",
      monthly_cap_flag: showMonthlyCap ? "Yes" : "No",
      daily_cap_flag: showDailyCap ? "Yes" : "No",
      weekly_cap_flag: showWeeklyCap ? "Yes" : "No",
    };

    try {
      const token = localStorage.getItem("token");
      const url = editingTarget
        ? `${API_URL}/campaigns/${campaign.id}/targets/${editingTarget.id}`
        : `${API_URL}/campaigns/${campaign.id}/targets`;
      const method = editingTarget ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (data.success) {
        fetchTargets();
        setFormData({
          buyer_id: "",
          targetName: "",
          targetNumber: "",
          maxConcurrency: "",
          monthlyCap: "",
          dailyCap: "",
          weeklyCap: "",
          connectionTimeout: "60",
          priority: "",
        });
        setShowForm(false);
        setEditingTarget(null);
      } else {
        alert(data.error || data.message || "Failed to save target");
      }
    } catch (error) {
      console.error("Error saving target:", error);
      alert("Error saving target: " + error.message);
    }
  };

  const handlePriorityUpdate = async (targetId, newPriority) => {
    if (!newPriority || isNaN(newPriority) || newPriority < 1) {
      alert("Please enter a valid priority number greater than 0");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${API_URL}/campaigns/${campaign.id}/targets/${targetId}`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ priority: parseInt(newPriority) }),
        }
      );

      const data = await response.json();
      if (data.success) {
        fetchTargets();
        setEditingPriority(null);
        setNewPriority("");
      } else {
        alert(data.error || "Failed to update priority");
      }
    } catch (error) {
      console.error("Error updating priority:", error);
      alert("Error updating priority");
    }
  };

  const handleStatusToggle = async (targetId, currentStatus) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${API_URL}/campaigns/${campaign.id}/targets/${targetId}/toggle-status`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await response.json();
      if (data.success) {
        fetchTargets();
      } else {
        alert(data.error || "Failed to toggle status");
      }
    } catch (error) {
      console.error("Error toggling status:", error);
      alert("Error toggling status");
    }
  };

  const handleWeightUpdate = async (targetId, newWeight) => {
    if (!newWeight || isNaN(newWeight) || newWeight < 0) {
      alert("Please enter a valid weight number (0 or greater)");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${API_URL}/campaigns/${campaign.id}/targets/${targetId}/weight`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ weight: parseInt(newWeight) }),
        }
      );

      const data = await response.json();
      if (data.success) {
        fetchTargets();
        setEditingWeight(null);
        setNewWeight("");
      } else {
        alert(data.error || "Failed to update weight");
      }
    } catch (error) {
      console.error("Error updating weight:", error);
      alert("Error updating weight");
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-80 flex items-center justify-center z-50 overflow-y-auto p-4 custom-scrollbar">
      <div className="bg-white p-4 rounded-lg shadow-lg max-w-3xl max-h-[90vh] overflow-y-auto custom-scrollbar">
        <h2 className="text-black flex justify-center font-bold text-md mb-2 sticky top-0">
          Assign Target {campaign.campaignName}
        </h2>

        {!showForm ? (
          <>
            <div className="mb-4 overflow-x-auto">
              {editingWeight !== null && (
                <div className="text-xs text-yellow-800 mb-2 p-2 bg-yellow-100 rounded">
                  Note: Please make sure Weight is set to "Yes" in campaign settings. To check this, click on Edit button.
                </div>
              )}
              <table className="w-full text-left text-black">
                <thead>
                  <tr>
                    <th className="border-b font-semibold border-gray-700 p-2">
                      Target Name
                    </th>
                    <th className="border-b font-semibold border-gray-700 p-2">
                      Target Number
                    </th>
                    <th className="border-b font-semibold border-gray-700 p-2">
                      Priority
                    </th>
                    <th className="border-b font-semibold border-gray-700 p-2">
                      Weight  
                    </th>
                    <th className="border-b font-semibold border-gray-700 p-2">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {targets.map((target) => (
                    <tr key={target.id} className="border-b border-gray-700">
                      <td className="p-2">{target.target_name}</td>
                      <td className="p-2">{target.target_number}</td>
                      <td className="p-2">
                        {editingPriority === target.id ? (
                          <div className="flex items-center space-x-2">
                            <input
                              type="number"
                              value={newPriority}
                              onChange={(e) => setNewPriority(e.target.value)}
                              className="bg-gray-700 text-white p-1 rounded w-20"
                              min="1"
                            />
                            <button
                              onClick={() =>
                                handlePriorityUpdate(target.id, newPriority)
                              }
                              className="text-green-500 hover:text-green-400"
                            >
                              ✓
                            </button>
                            <button
                              onClick={() => {
                                setEditingPriority(null);
                                setNewPriority("");
                              }}
                              className="text-red-500 hover:text-red-400"
                            >
                              ✕
                            </button>
                          </div>
                        ) : (
                          <div
                            className="cursor-pointer hover:text-blue-400"
                            onClick={() => {
                              setEditingPriority(target.id);
                              setNewPriority(target.priority.toString());
                            }}
                          >
                            {target.priority}
                          </div>
                        )}
                      </td>
                      <td className="p-2">
                        {editingWeight === target.id ? (
                          <div className="flex flex-col space-y-2">
                            <div className="flex items-center space-x-2">
                              <input
                                type="number"
                                value={newWeight}
                                onChange={(e) => setNewWeight(e.target.value)}
                                className="bg-gray-700 text-white p-1 rounded w-20"
                                min="0"
                              />
                              <button
                                onClick={() => handleWeightUpdate(target.id, newWeight)}
                                className="text-green-500 hover:text-green-400"
                              >
                                ✓
                              </button>
                              <button
                                onClick={() => {
                                  setEditingWeight(null);
                                  setNewWeight("");
                                }}
                                className="text-red-500 hover:text-red-400"
                              >
                                ✕
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div
                            className="cursor-pointer hover:text-blue-400"
                            onClick={() => {
                              setEditingWeight(target.id);
                              setNewWeight(target.weight?.toString() || "0");
                            }}
                          >
                            {target.weight || 0}%
                          </div>
                        )}
                      </td>
                      <td className="p-2">
                        <div className="flex items-center">
                          <Switch
                            checked={target.status === "active"}
                            onChange={() =>
                              handleStatusToggle(target.id, target.status)
                            }
                            className={`${
                              target.status === "active"
                                ? "bg-green-600"
                                : "bg-gray-400"
                            } relative inline-flex h-4 w-8 items-center rounded-full transition-colors focus:outline-none`}
                          >
                            <span
                              className={`${
                                target.status === "active"
                                  ? "translate-x-4"
                                  : "translate-x-1"
                              } inline-block h-3 w-3 transform rounded-full bg-white transition-transform`}
                            />
                          </Switch>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!targets.length && (
                    <tr>
                      <td colSpan="4" className="text-center text-gray-400 p-2">
                        No targets added yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end space-x-4">
              <button
                onClick={onClose}
                className="bg-gray-500 text-white px-4 py-2 rounded-lg"
              >
                Close
              </button>
              <button
                onClick={() => setShowForm(true)}
                className="bg-green-600 text-white px-4 py-2 rounded-lg"
              >
                Add Target
              </button>
            </div>
          </>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-1 max-w-sm mx-auto">
            <div className="space-y-1">
              <div>
                <label className="text-sm font-semibold text-gray-900">
                  Buyer *
                </label>
                <select
                  name="buyer_id"
                  value={formData.buyer_id}
                  onChange={handleInputChange}
                  className="p-1 bg-gray-200 text-gray-900 rounded-lg w-full"
                  required
                >
                  <option value="" className="text-xs">

                    Select Buyer
                  </option>
                  {buyers.map((buyer) => (
                    <option
                      key={buyer.id}
                      value={buyer.id}
                      data-testid={`buyer-option-${buyer.id}`}
                    >
                      {buyer.name} ({buyer.email}) - ID: {buyer.id}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-900">
                  Target Name *
                </label>
                <input

                  type="text"
                  name="targetName"
                  value={formData.targetName}
                  onChange={handleInputChange}
                  className="p-1 bg-gray-200 text-gray-900 rounded-lg w-full"
                  required
                />

              </div>
              <div>
                <label className="text-sm font-semibold text-gray-900">
                  Target Number *
                </label>
                <input
                  type="number"
                  name="targetNumber"
                  value={formData.targetNumber}
                  onChange={handleInputChange}
                  className="p-1 bg-gray-200 text-gray-900 rounded-lg w-full"
                  required
                  min="1"
                />
              </div>
              <div className="flex items-center justify-between">
                <label className="text-sm font-semibold text-gray-900">
                  Max Concurrency
                </label>
                <input
                  type="checkbox"
                  checked={showMaxConcurrency}
                  onChange={() => setShowMaxConcurrency(!showMaxConcurrency)}
                  className="form-checkbox h-4 w-4 text-green-600"
                />
              </div>
              {showMaxConcurrency && (
                <div>
                  <input
                    type="number"
                    name="maxConcurrency"
                    value={formData.maxConcurrency}
                    onChange={handleInputChange}
                    className="p-1 bg-gray-200 text-gray-900 rounded-lg w-full"
                    
                    min="1"
                  />
                </div>
              )}

              {/* Monthly Cap */}
              <div className="flex items-center justify-between">
                <label className="text-sm font-semibold text-gray-900">
                  Monthly Cap
                </label>

                <input
                  type="checkbox"
                  checked={showMonthlyCap}
                  onChange={() => setShowMonthlyCap(!showMonthlyCap)}
                  className="form-checkbox h-4 w-4 text-green-600"
                />
              </div>
              {showMonthlyCap && (
                <div>
                  <input
                    type="number"
                    name="monthlyCap"
                    value={formData.monthlyCap}
                    onChange={handleInputChange}
                    className="p-1 bg-gray-200 text-gray-900 rounded-lg w-full"
                    min="1"
                  />
                </div>

              )}

              {/* Daily Cap */}
              <div className="flex items-center justify-between">
                <label className="text-sm font-semibold text-gray-900">
                  Daily Cap
                </label>

                <input
                  type="checkbox"
                  checked={showDailyCap}
                  onChange={() => setShowDailyCap(!showDailyCap)}
                  className="form-checkbox h-4 w-4 text-green-600"
                />
              </div>
              {showDailyCap && (
                <div>
                  <input
                    type="number"
                    name="dailyCap"
                    value={formData.dailyCap}
                    onChange={handleInputChange}
                    className="p-1 bg-gray-200 text-gray-900 rounded-lg w-full"
                    min="1"
                  />
                </div>

              )}

              {/* Weekly Cap */}
              <div className="flex items-center justify-between">
                <label className="text-sm font-semibold text-gray-900">
                  Weekly Cap
                </label>

                <input
                  type="checkbox"
                  checked={showWeeklyCap}
                  onChange={() => setShowWeeklyCap(!showWeeklyCap)}
                  className="form-checkbox h-4 w-4 text-green-600"
                />
              </div>
              {showWeeklyCap && (
                <div>
                  <input
                    type="number"
                    name="weeklyCap"
                    value={formData.weeklyCap}
                    onChange={handleInputChange}
                    className="p-1 bg-gray-200 text-gray-900 rounded-lg w-full"
                    min="1"
                  />
                </div>

              )}

              <div>
                <label className="text-sm font-semibold text-gray-900">
                  Connection Timeout *
                </label>
                <input
                  type="number"
                  name="connectionTimeout"
                  value={formData.connectionTimeout}
                  onChange={handleInputChange}
                  className="p-1 bg-gray-200 text-gray-900 rounded-lg w-full"
                  required
                  min="1"
                  placeholder="60"
                />
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-900">
                  Priority *
                </label>

                <input
                  type="number"
                  name="priority"
                  value={formData.priority}
                  onChange={handleInputChange}
                  className="p-1 bg-gray-200 text-gray-900 rounded-lg w-full"
                  required
                  min="1"
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-2 pt-2 pb-2">
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingTarget(null);
                  setFormData({
                    buyer_id: "",
                    targetName: "",
                    targetNumber: "",
                    maxConcurrency: "",
                    monthlyCap: "",
                    dailyCap: "",
                    weeklyCap: "",
                    connectionTimeout: "60",
                    priority: "",
                  });
                }}
                className="bg-gray-500 text-white px-2 py-1 rounded-lg hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="bg-green-600 text-white px-2 py-1 rounded-lg hover:bg-green-700"
              >
                {editingTarget ? "Update Target" : "Save Target"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default AssignTargetPopup;
